import { createFileRoute, useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { Loader } from '@/components/ui/loader';
import {
  SettingsPageHeader,
  GeneralSettingsCard,
  SecuritySettingsCard,
  NotificationSettingsCard,
  DangerZoneCard,
  DeleteOrganizationModal,
  useOrganizationSettings,
  useUpdateOrganization,
  useDeleteOrganization,
} from './-components/settings';
import { useOrganizationSubscription } from '@/hooks/useOrganizationSubscription';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { loadAuthGuard } from '@/lib/auth-helpers';
import { trpcClient } from '@/router';

export const Route = createFileRoute('/_authenticated/org/$orgId/settings')({
  loader: loadAuthGuard<{ orgId: string }>(async ({ params, context, ensureQueryData, sessionReady }) => {
    const { orgId } = params;
    const { trpc } = context;

    if (!sessionReady) {
      return { orgId };
    }
    
    // Preload organizations to get organizationId from slug
    const orgsData = await ensureQueryData(
      trpc.organizations.getAll.queryOptions(undefined),
    );
    
    // Find organization by slug
    const orgs = (orgsData as { organizations?: Array<{ id: string; slug: string }> })?.organizations || [];
    const organization = orgs.find((o) => o.slug === orgId || o.id === orgId);
    const organizationId = organization?.id;
    
    // Preload organization settings and subscription if we have organizationId
    if (organizationId) {
      await ensureQueryData(
        trpc.organizationSettings.get.queryOptions({ organizationId }),
      );
      await ensureQueryData(
        trpc.organizationSubscription.getSubscription.queryOptions({ organizationId }),
      );
    }
    
    return { orgId };
  }),
  component: OrganizationSettingsPage,
});

function OrganizationSettingsPage() {
  const { orgId } = useParams({ from: '/_authenticated/org/$orgId/settings' });
  const { trpc } = useRouteContext({ from: '__root__' });
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  
  const { data: settings, isLoading: isLoadingSettings } = useOrganizationSettings(orgId);
  const { data: subscriptionData } = useOrganizationSubscription(orgId);
  const updateOrganizationMutation = useUpdateOrganization();
  const deleteOrganizationMutation = useDeleteOrganization();
  
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  // Mutation for updating security/notification settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (data: {
      security?: {
        mfaRequired?: boolean;
        sessionTimeout?: number;
        ipWhitelist?: string[];
        allowedDomains?: string[];
      };
      notifications?: {
        email?: boolean;
        slack?: boolean;
        discord?: boolean;
        webhook?: boolean;
      };
    }) => {
      return await trpcClient.organizationSettings.update.mutate({
        organizationId: orgId!,
        ...data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trpc.organizationSettings.get.queryKey({ organizationId: orgId! }) });
      toast.success(t('settings.security.toast.success'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('settings.security.toast.error'));
    },
  });

  // Determine current plan and check for active paid subscription
  const currentPlan = subscriptionData?.plan.slug || 'free';
  const hasActivePaidSubscription = 
    subscriptionData?.subscription.status === 'active' && 
    currentPlan !== 'free';

  const handleUpdateGeneral = async (data: {
    name?: string;
    description?: string;
    website?: string;
    timezone?: string;
    language?: string;
  }) => {
    await updateOrganizationMutation.mutateAsync({
      organizationId: orgId!,
      ...data,
    });
  };

  const handleUpdateSecurity = async (data: {
    mfaRequired?: boolean;
    sessionTimeout?: number;
    ipWhitelist?: string[];
    allowedDomains?: string[];
  }) => {
    await updateSettingsMutation.mutateAsync({
      security: data,
    });
  };

  const handleUpdateNotifications = async (data: {
    email?: boolean;
    slack?: boolean;
    discord?: boolean;
    webhook?: boolean;
  }) => {
    await updateSettingsMutation.mutateAsync({
      notifications: data,
    });
  };

  const handleDeleteOrganization = async () => {
    if (!settings) return;
    
    await deleteOrganizationMutation.mutateAsync({
      organizationId: orgId!,
      confirmationName: settings.name,
    });
    setDeleteModalOpen(false);
  };

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader variant="spinner" size="lg" color="accent" text="Loading settings..." />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Failed to load settings</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SettingsPageHeader />

      {/* General Settings */}
      <GeneralSettingsCard
        settings={{
          name: settings.name,
          slug: settings.slug,
          description: settings.description,
          website: settings.website || '',
          timezone: settings.timezone,
          language: settings.language,
        }}
        onSave={handleUpdateGeneral}
        isSaving={updateOrganizationMutation.isPending}
      />

      {/* Security Settings */}
      <SecuritySettingsCard
        settings={settings.security}
        onSave={handleUpdateSecurity}
        isSaving={updateSettingsMutation.isPending}
      />

      {/* Notification Settings */}
      <NotificationSettingsCard
        settings={settings.notifications}
        onSave={handleUpdateNotifications}
        isSaving={updateSettingsMutation.isPending}
      />

      {/* Danger Zone */}
      <DangerZoneCard
        organizationName={settings.name}
        currentPlan={currentPlan}
        onDelete={() => setDeleteModalOpen(true)}
        hasActivePaidSubscription={hasActivePaidSubscription}
      />

      {/* Delete Organization Modal */}
      <DeleteOrganizationModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        organizationName={settings.name}
        organizationSlug={orgId}
        currentPlan={currentPlan}
        onConfirm={handleDeleteOrganization}
        isDeleting={deleteOrganizationMutation.isPending}
        hasActivePaidSubscription={hasActivePaidSubscription}
      />
    </div>
  );
}
